/**
 * EditorialStatusChip — the unified status-chip primitive: a small-caps mono
 * tag with a soft status-tinted fill, for the redesign's two chip families:
 *
 *   RAG verdict   — GREEN · AMBER · RED   (go / conditional / no-go)
 *   signal state  — CONFIRMED · PENDING · OPEN  (the "SIGNALS" rows on p5/p6)
 *
 * Reference: `~/Desktop/Redhead_BTM_Feasibility_Redesign_1.pdf` — the
 * "CONFIRMED / PENDING / OPEN" signal chips and the GREEN/AMBER/RED verdict
 * chips. Re-skinned to OUR theme tokens (`roleColor`) — NO amber baked in: the
 * chip ink + fill derive from the active preset's positive/warning/negative
 * roles, so GREEN follows `positiveColor`, AMBER `warningColor`, RED
 * `negativeColor` in every theme.
 *
 * Consolidates the badge tones: this is the FILLED chip (soft status wash +
 * status ink), where `EditorialBadge` is the bare/outline inline tag. Both read
 * the same status roles, so a section can use `EditorialBadge` for an inline
 * verified-tag and `EditorialStatusChip` for a leading verdict/signal chip and
 * they stay tonally consistent.
 *
 * `status` is the semantic input — a verdict ('green'|'amber'|'red') OR a
 * signal state ('confirmed'|'pending'|'open'); each maps to a status role and a
 * default uppercase label (overridable via `children`). On a dark
 * instrument-panel pass `surface='dark'` to route the ink/fill through
 * `onInstrument()`.
 *
 * Theme tokens read: the status role ink (positive/warning/negative) + a
 * low-alpha fill of the same role via `roleColorAlpha`. Mono via
 * --report-body-font. Pure / print-safe (no shadow, no animation).
 */
import React from 'react';
import type { ReportTheme } from '../../../../types/reportTheme';
import {
  roleColor,
  roleColorAlpha,
  onInstrument,
  withAlpha,
  type ReportColorRole,
} from '../../../../lib/reportRoleColors';
import { REPORT_TEXT_PX } from '../../../../lib/reportSectionHeading';

const MONO = 'var(--report-body-font, inherit)';

/** Verdict (RAG) statuses + signal-state statuses, unified. */
export type EditorialChipStatus =
  | 'green'
  | 'amber'
  | 'red'
  | 'neutral'
  | 'confirmed'
  | 'pending'
  | 'open';

export type EditorialStatusChipSurface = 'light' | 'dark';

export interface EditorialStatusChipProps {
  theme: ReportTheme;
  status: EditorialChipStatus;
  /** Override the default uppercase label (e.g. a [Ref] suffix or count). */
  children?: React.ReactNode;
  /** 'dark' routes the ink/fill through onInstrument() for the dark panel. */
  surface?: EditorialStatusChipSurface;
  /** A bare ink chip (no soft fill) — for dense inline rows. */
  ghost?: boolean;
  className?: string;
  style?: React.CSSProperties;
  title?: string;
}

/** Each status → a status ROLE (theme-token ink) + a default label. */
const STATUS_ROLE: Record<EditorialChipStatus, ReportColorRole> = {
  green: 'positive',
  amber: 'warning',
  red: 'negative',
  neutral: 'muted',
  confirmed: 'positive',
  pending: 'warning',
  open: 'negative',
};

const DEFAULT_LABEL: Record<EditorialChipStatus, string> = {
  green: 'Green',
  amber: 'Amber',
  red: 'Red',
  neutral: 'N/A',
  confirmed: 'Confirmed',
  pending: 'Pending',
  open: 'Open',
};

export function EditorialStatusChip({
  theme,
  status,
  children,
  surface = 'light',
  ghost = false,
  className,
  style,
  title,
}: EditorialStatusChipProps): React.ReactElement {
  const role = STATUS_ROLE[status];
  const dark = surface === 'dark';
  const ink = dark ? onInstrument(theme, role) : roleColor(theme, role);
  // Soft fill: a low-alpha wash of the SAME status ink. On the dark panel the
  // wash derives from the ON-DARK (lightened) ink so it reads as a faint tint
  // of the visible chip color, not the dark light-surface role. Either way the
  // color is theme-token-derived — no raw hex.
  const fill = ghost
    ? undefined
    : dark
      ? withAlpha(ink, 0.22)
      : roleColorAlpha(theme, role, 0.12);

  return (
    <span
      className={className}
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontFamily: MONO,
        fontSize: REPORT_TEXT_PX.micro,
        fontWeight: 600,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: ink,
        background: fill,
        padding: ghost ? undefined : '2px 7px',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {children ?? DEFAULT_LABEL[status]}
    </span>
  );
}
